// src/pages/TransactionHistoryPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDownCircle, ArrowUpCircle, X, ChevronLeft } from 'lucide-react';
import { useTransactions } from '../hooks/useTransactions';
import transactionService from '../services/transaction.service';
import { formatCurrency } from '../utils/helpers';

export default function TransactionHistoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { transactions, loading: allTransactionsLoading } = useTransactions();
  
  const [investmentTransactions, setInvestmentTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Get filter from navigation state
  const filterInvestmentId = location.state?.filterInvestmentId;
  const filterInvestmentName = location.state?.filterInvestmentName;

  // Fetch investment-specific transactions when filter is applied
  useEffect(() => {
    const fetchInvestmentTransactions = async () => {
      if (filterInvestmentId) {
        setLoading(true);
        try {
          const data = await transactionService.getInvestmentTransactions(filterInvestmentId);
          console.log('Investment transactions:', data);
          
          // Transform backend data to match frontend format
          const transformedData = data.map(tx => ({
            id: tx.transactionId || tx.id,
            investmentId: tx.investmentId,
            investmentName: filterInvestmentName || tx.investmentName || tx.type,
            type: tx.transactionType.toLowerCase(),
            amount: tx.amount,
            date: tx.transactionDate || tx.createdAt
          }));
          
          setInvestmentTransactions(transformedData);
        } catch (err) {
          console.error('Error fetching investment transactions:', err);
          setInvestmentTransactions([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchInvestmentTransactions();
  }, [filterInvestmentId, filterInvestmentName]);
  
  // Use investment-specific transactions if filtering, otherwise use all transactions
  const filteredTransactions = filterInvestmentId 
    ? investmentTransactions
    : transactions;

  const clearFilter = () => {
    navigate('/transactions', { replace: true, state: {} });
    setInvestmentTransactions([]);
  };

  const getTotalDeposits = () => {
    return filteredTransactions
      .filter(tx => tx.type === 'deposit')
      .reduce((sum, tx) => sum + tx.amount, 0);
  };

  const getTotalWithdrawals = () => {
    return filteredTransactions
      .filter(tx => tx.type === 'withdrawal')
      .reduce((sum, tx) => sum + tx.amount, 0);
  };

  const isLoading = filterInvestmentId ? loading : allTransactionsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b141a]">
      {/* WhatsApp-style Header */}
      <div className="bg-[#1f2c34] -mx-4 lg:mx-0 lg:rounded-lg lg:mt-6 px-4 lg:px-6 py-4 lg:py-5 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 lg:gap-4 flex-1 min-w-0">
            <button
              onClick={() => navigate('/')}
              className="text-[#8696a0] p-1 -ml-1 active:bg-[#2a3942] hover:bg-[#2a3942] rounded-full transition-colors flex-shrink-0"
            >
              <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-white text-lg lg:text-2xl font-medium">Transaction History</p>
              <p className="text-[#8696a0] text-xs lg:text-sm truncate">
                {filterInvestmentId 
                  ? filterInvestmentName
                  : `${filteredTransactions.length} transactions`
                }
              </p>
            </div>
          </div>
          
          {filterInvestmentName && (
            <button 
              onClick={clearFilter}
              className="text-[#8696a0] p-2 active:bg-[#2a3942] hover:bg-[#2a3942] rounded-full transition-colors flex-shrink-0"
            >
              <X size={20} className="lg:w-6 lg:h-6" />
            </button>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto pb-20 px-4 lg:px-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4 py-4 lg:py-6">
          <div className="bg-[#1f2c34] rounded-lg p-3 lg:p-5">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-1 lg:mb-2">Total</p>
            <p className="text-white text-lg lg:text-2xl font-medium">{filteredTransactions.length}</p>
          </div>
          <div className="bg-[#1f2c34] rounded-lg p-3 lg:p-5 col-span-1 lg:col-span-2">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-1 lg:mb-2">Deposits</p>
            <p className="text-[#00a884] text-sm lg:text-xl font-medium break-words">
              {formatCurrency(getTotalDeposits())}
            </p>
          </div>
          <div className="bg-[#1f2c34] rounded-lg p-3 lg:p-5 col-span-1 lg:col-span-2">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-1 lg:mb-2">Withdrawals</p>
            <p className="text-orange-400 text-sm lg:text-xl font-medium break-words">
              {formatCurrency(getTotalWithdrawals())}
            </p>
          </div>
          <div className="bg-[#1f2c34] rounded-lg p-3 lg:p-5 hidden lg:block">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-1 lg:mb-2">Net Flow</p>
            <p className={`text-sm lg:text-xl font-medium ${
              getTotalDeposits() - getTotalWithdrawals() >= 0 ? 'text-[#00a884]' : 'text-orange-400'
            }`}>
              {formatCurrency(getTotalDeposits() - getTotalWithdrawals())}
            </p>
          </div>
        </div>

        {/* Transaction List */}
        <div>
          {filteredTransactions.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
              {[...filteredTransactions].reverse().map((tx) => (
                <div 
                  key={tx.id} 
                  className="bg-[#1f2c34] rounded-lg p-4 lg:p-5 hover:bg-[#253640] transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 lg:gap-4">
                    {/* Left side - Icon, Investment name and date */}
                    <div className="flex items-center gap-3 lg:gap-4 flex-1 min-w-0">
                      <div className={`flex-shrink-0 w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ${
                        tx.type === 'deposit' 
                          ? 'bg-[#00a884]/10' 
                          : 'bg-orange-500/10'
                      }`}>
                        {tx.type === 'deposit' ? (
                          <ArrowUpCircle className="text-[#00a884] w-6 h-6 lg:w-7 lg:h-7" />
                        ) : (
                          <ArrowDownCircle className="text-orange-400 w-6 h-6 lg:w-7 lg:h-7" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-base lg:text-lg font-normal truncate">
                          {tx.investmentName || tx.type}
                        </p>
                        <p className="text-[#8696a0] text-xs lg:text-sm mt-1">{tx.date}</p>
                      </div>
                    </div>
                    
                    {/* Right side - Amount */}
                    <div className="text-right flex-shrink-0">
                      <p className={`text-base lg:text-lg font-medium whitespace-nowrap ${
                        tx.type === 'deposit' ? 'text-[#00a884]' : 'text-orange-400'
                      }`}>
                        {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </p>
                      <p className="text-[#8696a0] text-xs lg:text-sm mt-1">
                        {tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#1f2c34] rounded-lg p-8 lg:p-16 text-center mt-4 max-w-2xl mx-auto">
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#00a884]/10 flex items-center justify-center mx-auto mb-4 lg:mb-6">
                <ArrowUpCircle className="w-8 h-8 lg:w-10 lg:h-10 text-[#00a884]" />
              </div>
              <h3 className="text-white text-base lg:text-2xl font-normal mb-2 lg:mb-3">No Transactions</h3>
              <p className="text-[#8696a0] text-sm lg:text-base mb-6 lg:mb-8">
                {filterInvestmentId 
                  ? `No transactions found for ${filterInvestmentName}` 
                  : 'Start by adding an investment'}
              </p>
              {filterInvestmentId && (
                <button 
                  onClick={clearFilter}
                  className="bg-[#00a884] text-white px-6 py-3 lg:px-8 lg:py-4 rounded-full font-medium text-sm lg:text-base active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors"
                >
                  View All Transactions
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}